import { Box, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DeviationStatus } from 'src/enums'
import { IDeviation } from 'src/generated-types/deviation'
import { IDeviationPriority } from 'src/generated-types/deviation-priority'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { priorityColorToken, statusColorToken } from './deviationStatus'

type Props = {
  deviation?: IDeviation
}

export const DeviationKeyFacts: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()

  // Mirror the type-flag derivation used by DeviationDetailSection.
  const typeLabels = useMemo(() => {
    const labels: string[] = []
    if (deviation?.visualDeviation) labels.push('visualDeviation')
    if (deviation?.abnormalWear) labels.push('abnormalWear')
    if (deviation?.measuredDeviation) labels.push('measuredDeviation')
    if (deviation?.normalWear) labels.push('normalWear')
    return labels
  }, [deviation?.visualDeviation, deviation?.abnormalWear, deviation?.measuredDeviation, deviation?.normalWear])

  // Completed date is the createdAt of the closing follow-up (same as DeviationDetailSection).
  const completedDate = deviation?.deviationFollowUps?.find(df => df.status)?.createdAt

  // `status` (boolean) is the authoritative closed flag across the app (drives the
  // "Lukket av" chip, disables actions, closes follow-ups). The deviationStatus enum
  // isn't reliably set to Complete, so prefer the boolean to avoid showing "Åpen"
  // on a closed deviation.
  const isClosed = !!deviation?.status

  const statusLabel = useMemo(() => {
    if (deviation?.deviationStatus === DeviationStatus.InApproval) {
      return t('pendingApproval', { ns: 'deviation' })
    }
    if (isClosed) return t('closed', { ns: 'common' })
    return t('open', { ns: 'common' })
  }, [isClosed, deviation?.deviationStatus, t])

  const statusColor =
    isClosed && deviation?.deviationStatus != DeviationStatus.InApproval
      ? MUIThemeColors.successMain
      : statusColorToken(deviation?.deviationStatus)

  const priorityLabel =
    deviation?.priority != null ? t(IDeviationPriority[deviation.priority as IDeviationPriority]) : undefined

  type Cell = { key: string; label: string; value: React.ReactNode }
  const cells: Cell[] = []

  // Status — colored dot + label
  cells.push({
    key: 'status',
    label: t('status', { ns: 'common' }),
    value: (
      <Flex.Row gap={0.75} sx={{ alignItems: 'center' }}>
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: getThemeColor(theme, statusColor),
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{statusLabel}</Typography>
      </Flex.Row>
    ),
  })

  // Prioritet — value colored via priorityColorToken
  if (priorityLabel) {
    cells.push({
      key: 'priority',
      label: t('priority', { ns: 'common' }),
      value: (
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 600,
            color: getThemeColor(theme, priorityColorToken(deviation?.priority)),
          }}
        >
          {priorityLabel}
        </Typography>
      ),
    })
  }

  // Type — deviation type label(s)
  if (typeLabels.length > 0) {
    cells.push({
      key: 'type',
      label: t('type', { ns: 'common' }),
      value: (
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
          {typeLabels.map(label => t(label)).join(', ')}
        </Typography>
      ),
    })
  }

  // Opprettet
  if (deviation?.createdAt) {
    cells.push({
      key: 'createdAt',
      label: t('createdAt', { ns: 'common' }),
      value: (
        <Typography sx={{ fontSize: '15px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {getFormattedDate(deviation.createdAt)}
        </Typography>
      ),
    })
  }

  // Frist (due date)
  if (deviation?.dueDate) {
    cells.push({
      key: 'dueDate',
      label: t('dueDate', { ns: 'common' }),
      value: (
        <Typography sx={{ fontSize: '15px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {getFormattedDate(deviation.dueDate)}
        </Typography>
      ),
    })
  }

  // Utført (completed date) — hidden entirely when missing
  if (completedDate) {
    cells.push({
      key: 'completedDate',
      label: t('completedDate', { ns: 'deviation' }),
      value: (
        <Typography sx={{ fontSize: '15px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {getFormattedDate(completedDate)}
        </Typography>
      ),
    })
  }

  return (
    <Box
      sx={{
        backgroundColor: getThemeColor(theme, MUIThemeColors.white),
        borderRadius: '8px',
        border: '1px solid #E6E8EA',
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      {cells.map((cell, index) => (
        <Box
          key={cell.key}
          sx={{
            padding: '10px 16px',
            borderLeft: index === 0 ? 'none' : '1px solid #E6E8EA',
            flex: { xs: '1 1 40%', md: '1 1 0' },
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: getThemeColor(theme, MUIThemeColors.secondaryMain),
              mb: 0.25,
            }}
          >
            {cell.label}
          </Typography>
          {cell.value}
        </Box>
      ))}
    </Box>
  )
}
